// CaptureHero — forest hero card used at the top of the Building,
// Room, and Site-level Capture screens (design handoff §2.1, §3.1,
// §4.1).
//
// Layout is brand-locked: forest background with a soft lime radial
// glow, an eyebrow tag (lime on tinted-lime pill), Fraunces display
// name with optional FLOC code in mono underneath, and an action row
// of up to two buttons. The redesign drops progress bars throughout
// per product direction so the hero never carries a meter; counts
// (e.g. "12 / 56 assets") are surfaced inline as a sub-line when a
// caller has them.
//
// Renders a polymorphic 1-2-button action row. The primary slot uses
// the lime-on-forest treatment (mirroring `cc-hero-btn--primary` in
// the design CSS); the optional secondary slot uses the muted glass
// treatment. Each action is a plain `<button>` so callers wire their
// own handlers and disabled state.

import type { ReactElement, ReactNode } from 'react'

export interface CaptureHeroAction {
  label: string
  onClick: () => void
  icon?: ReactNode
  // When `disabled`, the button keeps its visual treatment but is
  // not interactable. Used for the "Notes" placeholder until notes
  // infrastructure lands.
  disabled?: boolean
  ariaLabel?: string
}

// Small icon-only affordance in the top-right corner of the hero
// card. Used by Building capture for the destructive "Cancel
// session" action — pulled out of the sticky bottom bar so the
// primary "Mark complete" CTA gets the full bar width and the
// destructive control sits visually away from it. The icon-only
// treatment also signals "secondary" against the lime primary
// button at the bottom.
export interface CaptureHeroHeaderAction {
  label: string
  icon: ReactNode
  onClick: () => void
  disabled?: boolean
  testId?: string
}

export interface CaptureHeroProps {
  eyebrow: string
  name: string
  // FLOC / room type / floor — small mono-spaced sub-line. Optional
  // (omitted on rooms when there's no useful identifier).
  subtitle?: string | null
  // Plain-text sub-line under the FLOC, e.g. "12/56 assets". The
  // redesign drops the visual progress bar but keeps the count for
  // glanceable progress.
  countLine?: string | null
  primaryAction?: CaptureHeroAction
  secondaryAction?: CaptureHeroAction
  // Optional icon-only button in the hero's top-right corner. Not
  // rendered when undefined — keeps Room and Site hero callers
  // unchanged.
  headerAction?: CaptureHeroHeaderAction
}

export function CaptureHero({
  eyebrow,
  name,
  subtitle,
  countLine,
  primaryAction,
  secondaryAction,
  headerAction,
}: CaptureHeroProps): ReactElement {
  return (
    <section
      data-testid="capture-hero"
      className="relative overflow-hidden rounded-2xl bg-[color:var(--color-cc-forest)] p-5 text-[color:var(--color-cc-cream)] sm:p-6"
    >
      {/* Soft lime radial glow in the top-right corner — pulled from
           the `.cc-hero::after` pseudo-element. Pointer-events disabled
           so it never blocks button taps that overlap it. The gradient
           is built from the design-token lime via color-mix so the
           hex/rgb literal stays out of source. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-10 -right-10 h-36 w-36 rounded-full"
        style={{
          background:
            'radial-gradient(circle, color-mix(in srgb, var(--color-cc-lime) 22%, transparent) 0%, transparent 65%)',
        }}
      />
      {headerAction !== undefined ? (
        <HeroHeaderActionButton action={headerAction} />
      ) : null}
      <span className="relative inline-flex items-center gap-1.5 rounded-full bg-[color:var(--color-cc-lime)]/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-cc-lime)]">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--color-cc-lime)]" />
        {eyebrow}
      </span>
      <h2 className="relative mt-3 break-words font-display text-2xl font-semibold leading-tight text-[color:var(--color-cc-cream)] sm:text-[26px]">
        {name}
      </h2>
      {subtitle ? (
        <p className="relative mt-1 break-words font-mono text-[11px] tracking-wider text-[color:var(--color-cc-cream)]/55">
          {subtitle}
        </p>
      ) : null}
      {countLine ? (
        <p className="relative mt-2 text-sm text-[color:var(--color-cc-cream)]/75">
          {countLine}
        </p>
      ) : null}
      {primaryAction !== undefined || secondaryAction !== undefined ? (
        <div className="relative mt-4 flex gap-2">
          {primaryAction !== undefined ? (
            <HeroActionButton action={primaryAction} variant="primary" />
          ) : null}
          {secondaryAction !== undefined ? (
            <HeroActionButton action={secondaryAction} variant="ghost" />
          ) : null}
        </div>
      ) : null}
    </section>
  )
}

interface HeroActionButtonProps {
  action: CaptureHeroAction
  variant: 'primary' | 'ghost'
}

function HeroActionButton({
  action,
  variant,
}: HeroActionButtonProps): ReactElement {
  const isPrimary = variant === 'primary'
  return (
    <button
      type="button"
      onClick={action.onClick}
      disabled={action.disabled}
      aria-label={action.ariaLabel ?? action.label}
      className={[
        'inline-flex min-h-11 flex-1 items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold transition-colors',
        isPrimary
          ? 'border border-[color:var(--color-cc-lime)] bg-[color:var(--color-cc-lime)] text-[color:var(--color-cc-forest)] hover:brightness-95 disabled:opacity-50'
          : 'border border-[color:var(--color-cc-cream)]/20 bg-[color:var(--color-cc-cream)]/10 text-[color:var(--color-cc-cream)] hover:bg-[color:var(--color-cc-cream)]/15 disabled:opacity-50',
      ].join(' ')}
    >
      {action.icon !== undefined ? action.icon : null}
      {action.label}
    </button>
  )
}

interface HeroHeaderActionButtonProps {
  action: CaptureHeroHeaderAction
}

// Absolute-positioned icon button that sits above the lime radial
// glow (z-10) in the hero's top-right corner. 44×44 hit target
// satisfies the mobile touch-target invariant (AGENTS.md §10). The
// lime glow already has `pointer-events-none` so taps over the
// overlap region always reach this button.
function HeroHeaderActionButton({
  action,
}: HeroHeaderActionButtonProps): ReactElement {
  return (
    <button
      type="button"
      onClick={action.onClick}
      disabled={action.disabled}
      aria-label={action.label}
      title={action.label}
      data-testid={action.testId}
      className="absolute right-3 top-3 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full bg-[color:var(--color-cc-cream)]/10 text-[color:var(--color-cc-cream)] hover:bg-[color:var(--color-cc-cream)]/20 disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-cc-lime)]"
    >
      {action.icon}
    </button>
  )
}
